import React, { useContext, useState, useEffect, useMemo } from "react";
import { useRoomTimeline } from "../../hooks";
import { DateHeaderLine } from "../styled";
import { ChatContext } from "../../ChatContext";
import QueuedMessagePreview from "../QueuedMessagePreview";
import { MatrixEventEvent } from "matrix-js-sdk";
import { EventStatus } from "matrix-js-sdk/lib/models/event-status";
import { getFilteredTimelineForUser } from "../../utils/filteredTimeline";
import {
    CALL_ANSWER,
    CALL_CANDIDATES,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_NOTE,
    CALL_REJECT,
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_NOTIFICATION,
    MEETING_PARTICIPANT_NOTIFICATION,
    ROOM_ENCRYPTED,
    ROOM_MEMBER,
    ROOM_MESSAGE
} from "src/constants/matrixEventTypes";
import {
    getMeetingEventMetadata,
    SHARED_CALL_SUMMARY_FIELD,
    WEB_CALL_SUMMARY_FIELD
} from "src/scenes/Chat/utils/meetingCallEventContract";

const CALL_SUMMARY_MESSAGE_FIELD = WEB_CALL_SUMMARY_FIELD;
const CALL_NOTE_MESSAGE_FIELD = "com.memotivationapp.call_note";

const NON_RESENDABLE_CALL_EVENT_TYPES = new Set([
    CALL_ANSWER,
    CALL_CANDIDATES,
    CALL_HANGUP,
    CALL_INVITE,
    CALL_NOTE,
    CALL_REJECT,
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_NOTIFICATION,
    MEETING_PARTICIPANT_NOTIFICATION
]);

const RENDERABLE_TIMELINE_EVENT_TYPES = [
    ROOM_MESSAGE,
    ROOM_ENCRYPTED,
    ROOM_MEMBER,
    CALL_INVITE,
    CALL_HANGUP,
    CALL_REJECT,
    MATRIX_RTC_CALL_NOTIFY,
    MATRIX_RTC_NOTIFICATION
];

const isResendableFailedMessage = event => {
    const content = event.getContent?.() || {};

    return !NON_RESENDABLE_CALL_EVENT_TYPES.has(event.getType?.()) &&
        !content[CALL_NOTE_MESSAGE_FIELD] &&
        !content[CALL_SUMMARY_MESSAGE_FIELD] &&
        !content[SHARED_CALL_SUMMARY_FIELD];
};

const ChatRoomTimeline = (props) => {
    const {
        useSendMessage: ctxUseSendMessage,
        useMatrixChatList: ctxUseMatrixChatList,
        useChatCalls: ctxUseChatCalls
    } = useContext(ChatContext);
    const { matrixClient, roomTimeline, currentChat, currentMatrixUserId } = props;
    const currentRoomId = currentChat?.matrixRoomId;
    const { renderTimelineEvent } = useRoomTimeline(
        matrixClient,
        roomTimeline,
        currentChat,
        currentMatrixUserId,
        ctxUseSendMessage,
        ctxUseMatrixChatList,
        ctxUseChatCalls
    );
    const [refresh, setRefresh] = useState(0); 

    const replacedEventIds = ctxUseSendMessage?.replacedEventIds || new Set();
    const groupMessagesBySender = (timeline) => {
        const groupedMessages = [];
        for (let i = 0; i < timeline.length; i++) {
            const event = timeline[i];
            if (!event.getSender() || event.isRedacted() || replacedEventIds.has(event.getId()))
                continue;

            const currentSender = event.getSender();
            const nextEvent = timeline[i + 1];
            const nextSender = nextEvent?.getSender();

            const isLastOfGroup = currentSender !== nextSender;

            groupedMessages.push({
                event,
                showAvatar: isLastOfGroup,
            });
        }
        return groupedMessages;
    };

    // Used to force re-render on membership and call-summary updates
    useEffect(() => {
        if (!matrixClient || !currentRoomId) {
            return undefined;
        }

        const handleTimelineRefresh = (event) => {
            const content = event.getContent?.() || {};
            const isCallSummary = Boolean(
                content[CALL_SUMMARY_MESSAGE_FIELD] || content[SHARED_CALL_SUMMARY_FIELD]
            );
            const currentRoomEvents = matrixClient.getRoom(currentRoomId)
                ?.getUnfilteredTimelineSet?.()
                ?.getLiveTimeline?.()
                ?.getEvents?.() || [];
            const isMeetingRoomNote = event.getType() === ROOM_MESSAGE &&
                content.msgtype === "m.text" &&
                currentRoomEvents.some(roomEvent =>
                    getMeetingEventMetadata(roomEvent.getContent?.() || {})?.matrixRoomId === event.getRoomId()
                );
            if (
                isMeetingRoomNote || (([ROOM_MEMBER, CALL_INVITE, CALL_HANGUP, CALL_REJECT,
                    MATRIX_RTC_CALL_NOTIFY, MATRIX_RTC_NOTIFICATION]
                    .includes(event.getType()) || isCallSummary) &&
                event.getRoomId() === currentRoomId)
            ) {
                setRefresh((prev) => prev + 1);
            }
        };
        matrixClient.on("event", handleTimelineRefresh);
        matrixClient.on(MatrixEventEvent.Decrypted, handleTimelineRefresh);
        return () => {
            matrixClient.off("event", handleTimelineRefresh);
            matrixClient.off(MatrixEventEvent.Decrypted, handleTimelineRefresh);
        };
    }, [matrixClient, currentRoomId]);

    const queuedMessage = ctxUseSendMessage?.queued;
    const resendMessage = ctxUseSendMessage?.resendMessage;
    const baseUrl = matrixClient?.baseUrl;
    const resentEventIds = ctxUseSendMessage?.resentEventIds;

    const sortedFailedEvents = useMemo(() => {
        const map = new Map();

        roomTimeline
            ?.filter(event => event.status === EventStatus.NOT_SENT && isResendableFailedMessage(event))
            .forEach(event => {
                const content = event.getContent();
                const key = `${content?.msgtype}-${content?.body}`;
                // Skip this content if any matching message was already resent
                const hasBeenResent = roomTimeline.some(ev =>
                    resentEventIds.has(ev.getId()) &&
                    `${ev.getContent()?.msgtype}-${ev.getContent()?.body}` === key
                );
                if (hasBeenResent) return;
                const existing = map.get(key);
                if (!existing || event.getTs() < existing.getTs()) {
                    map.set(key, event); // keep oldest to match on resentEventIds
                }
            });

        return Array.from(map.values());
    }, [roomTimeline, resentEventIds]);

    const renderRoomTimeline = () => {
        if (!matrixClient || !currentRoomId || !Array.isArray(roomTimeline)) {
            return null;
        }

        //adding this logic, as we are still using the oldState of roomTimeline even thoughh we changed `currentChat`.
        const cachedRoomTimelineMatchesTheRoom = roomTimeline.filter(matrixEvent => matrixEvent.getRoomId() === currentRoomId);
        /* Displaying join/create chat messages here */
        const room = matrixClient.getRoom(currentRoomId);
        const fullTimeline = room?.getUnfilteredTimelineSet()?.getLiveTimeline()?.getEvents() || [];
        const combinedTimeline = [...fullTimeline, ...cachedRoomTimelineMatchesTheRoom];
        const updatedTimeline = Array.from(
            new Map(combinedTimeline.map(e => [e.getId(), e])).values()
        );
        const hasMessages = updatedTimeline.some(ev =>
            ["m.room.message", "m.room.encrypted"].includes(ev.getType())
        );

        let filteredTimeline = updatedTimeline;
        if (!hasMessages) {
            filteredTimeline = cachedRoomTimelineMatchesTheRoom.length
                ? cachedRoomTimelineMatchesTheRoom
                : fullTimeline;
        } else {

            if (room) {
                filteredTimeline = getFilteredTimelineForUser(room, updatedTimeline, currentMatrixUserId);
            } else {
                console.warn("Room not yet loaded. Showing unfiltered timeline.");
            }
        }

        filteredTimeline = filteredTimeline.filter(event => RENDERABLE_TIMELINE_EVENT_TYPES.includes(event.getType()));

        try
        {
            const groupedChats = sortByLatestEventTimestampAndGroupByDate(filteredTimeline);
            
            return (
                <>
                    {Object.entries(groupedChats).map(([groupIndexTimeString, group]) => {
                        // Group messages by sender with avatar info
                        const groupedWithAvatar = groupMessagesBySender(group);
                   
                        return (
                            <React.Fragment key={groupIndexTimeString}>
                                {groupedWithAvatar.map(({ event, showAvatar }, i) => {
                                    try {
                                        const previousItem = i > 0 ? groupedWithAvatar[i - 1] : null;
                                        const previousEvent = previousItem ? previousItem.event : null;

                                        const currentSender = event.getSender();
                                        const previousSender = previousEvent ? previousEvent.getSender() : null;

                                        const currentDate = new Date(event.getTs());
                                        const previousDate = previousEvent ? new Date(previousEvent.getTs()) : null;

                                        const currentMinute = `${currentDate.getHours()}:${currentDate.getMinutes()}`;
                                        const previousMinute = previousDate ? `${previousDate.getHours()}:${previousDate.getMinutes()}` : null;

                                        const isDifferentSender = currentSender !== previousSender;
                                        const isDifferentMinute = currentMinute !== previousMinute;

                                        // Show timestamp (different time, different sender OR this is the last message in the group)
                                        const isLastInGroup = i === groupedWithAvatar.length - 1;
                                        const shouldShowTimestamp = isDifferentMinute || isDifferentSender || isLastInGroup;

                                        return (
                                            <React.Fragment key={`${groupIndexTimeString}-${event.getId()}`}>
                                                {renderTimelineEvent(event, i, showAvatar, shouldShowTimestamp)}
                                            </React.Fragment>
                                        );
                                    } catch (eventError) {
                                        console.warn("Unable to render a room timeline event", event.getId?.(), eventError);
                                        return null;
                                    }
                                })}


                                <DateHeader title={formatDateComparison(groupIndexTimeString)} />
                            </React.Fragment>
                        );
                    })}
                </>
            );
        }
        catch (e) {
            console.warn("Unable to render the messages for this room", e);
        }
    };

    const formatDateComparison = (matrixEventDate) => {
        const myEventDate = new Date(matrixEventDate);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
      
        // Set hours, minutes, seconds, and milliseconds to 0 for accurate date comparison
        const myEventByDay = new Date(myEventDate.getFullYear(), myEventDate.getMonth(), myEventDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        
        if (myEventByDay.getTime() === todayOnly.getTime()) {
            return "today";
        } else if (myEventByDay.getTime() === yesterdayOnly.getTime()) {
            return "yesterday";
        } else {
            // Return the date in M d, Y format
            const month = myEventByDay.toLocaleString("en-US", { month: "long" });
            const day = myEventByDay.getDate();
            const year = myEventByDay.getFullYear();
            return `${month} ${day}, ${year}`;
        }
    };

    const sortByLatestEventTimestamp = (chats) => {
        return chats.sort((a, b) => {
            const timestampA = new Date(a.getTs());
            const timestampB = new Date(b.getTs());
            return timestampB - timestampA;
        });
    };

    const sortByLatestEventTimestampAndGroupByDate = (chats) => {

        const sortedChats = sortByLatestEventTimestamp(chats);

        const groupedChats = {};
        sortedChats.forEach((chat) => {
            const timestampUTC = new Date(chat.getTs());
            const dateString = timestampUTC.toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
            if (!groupedChats[dateString]) {
                groupedChats[dateString] = [];
            }
      
            groupedChats[dateString].push(chat);
        });

        return groupedChats;
    };

    return (
        <>
            {/* Show sending message if exists */}
            {queuedMessage?.status === "sending" && (
                <QueuedMessagePreview
                    queuedMessage={queuedMessage}
                    baseUrl={baseUrl}
                />
            )}

            {/* Show all failed messages */}
            {sortedFailedEvents.slice().reverse().map((event) => {
                const content = event.getContent();
                const stableKey = event.getId() || `${content?.msgtype}-${content?.body}-${event.getTs()}`;

                let message;
                let isFile = false;

                if (content?.info?.mimetype && content?.url) {
                    const file = new File([""], content?.body, {
                        type: content?.info?.mimetype,
                        lastModified: Date.now(),
                    });
                    message = {
                        file,
                        name: content?.body,
                        type: content?.info?.mimetype,
                        size: content?.info?.size || 0,
                        url: content?.url,
                        loading: false,
                    };
                    isFile = content?.info?.mimetype;
                } else {
                    message = content?.body;
                }

                return (
                    <QueuedMessagePreview
                        key={`failed-${stableKey}`}
                        queuedMessage={{ message, status: "failed", msgType: content?.msgtype, isFile,
                            isSending: false,
                            failureSource: "sendMessage",
                        }}
                        resendMessage={() => resendMessage(event)}
                        baseUrl={baseUrl}
                        originalEvent={event} 
                    />
                );
            })}

            {/* The rest of your timeline */}
            {matrixClient && currentRoomId && roomTimeline && renderRoomTimeline(refresh)}
        </>
    );
};

const DateHeader = ({ title }) => {
    return (
        <>
            <DateHeaderLine />
            <div style={{ 
                textAlign: "center", 
                size: "0.875rem", 
                textTransform: "capitalize",
                marginTop: "20px"
            }}>{title}</div>
        </>
    );
};

export default ChatRoomTimeline;
